import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { RoutingStateService } from '@brightside/brightside-ui-services';
import {
  CreditSetupVerifyResponse,
  CreditSetupVerifyStatus,
  FirebaseService,
  isTemplateFullScreen,
} from '@brightside-web/desktop/data-access/shared';
import { CreditSetupService } from '@brightside-web/desktop/data-access/credit';
import { Environment } from '@brightside-web/micro/core/environment';

import { BsAuthService, BsCacheService, BsChatService, BsHubService } from '@brightside-web/desktop/data-access/core-services';

import { from, Subscription } from 'rxjs';
import { Intercom } from '@supy-io/ngx-intercom';
import { AbstractCreditSetupComponent } from '../abstract-credit-setup.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'brightside-web-verify-otp',
    templateUrl: './verify-otp.component.html',
    styleUrls: ['./verify-otp.component.scss'],
    standalone: false
})
export class VerifyOtpComponent extends AbstractCreditSetupComponent implements OnInit, OnDestroy {
  phoneNumber = '';
  fourTwoNineError: boolean;
  codeForm = this.formBuilder.group({
    code: ['', [Validators.required, Validators.pattern('(\\d{6,10})')]],
  });
  codeErrorText = '';

  showThirdPartyBlurb = false;
  inOboarding: boolean;

  constructor(
    protected router: Router,
    protected routingState: RoutingStateService,
    protected activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private creditSetupService: CreditSetupService,
    protected analytics: FirebaseService,
    protected env: Environment,
    private intercom: Intercom,
    private translateService: TranslateService,
    private bsAuthService: BsAuthService,
    public bsCacheService: BsCacheService,
    private _bsChatService: BsChatService
  ) {
    super(router, routingState, activatedRoute, env, analytics);
  }

  ngOnInit() {
    super.ngOnInit();
    this.subscribeAndLoadProfile();

    this.codeForm.valueChanges.subscribe(() => {
      //Clear error message
      this.codeErrorText = '';
    });
    this.bsCacheService.getItem('ONBOARDING_FLOW_ACTIVE').then((flowActive) => {
      this.inOboarding = flowActive;
    });
  }

  private subscribeAndLoadProfile() {
    const handleSuccess = (data: any) => {
      const { attributes: currentUserInfo } = data;

      this.phoneNumber = currentUserInfo['phone_number'].slice(-10);
    };
    const handleFailure = () => {
      this.analytics.logEvent('error_shown', { 'error id': 'credit_failed_otp' });
    };

    this.sub.add(from(this.bsAuthService.fetchUserAttributes()).subscribe(handleSuccess, handleFailure));
  }

  private mismatchContinue() {
    this.handleRoute(true, ['credit-setup', 'creation'], { queryParams: { status: 'VERIFICATION_FAILED', statusCode: '429' } });
  }

  private verifyOtpCode() {
    const handleResponse = (resp: CreditSetupVerifyResponse) => {
      if (resp.apiResponse?.statusCode === 429) {
        this.fourTwoNineError = true;
      } else {
        switch (resp.verifyStatus) {
          case CreditSetupVerifyStatus.COMPLETE:
            this.analytics.logEvent('credit_otp_completed');
            this.completeRoute().then((resp) => {
              this.handleRouteForward(true, resp);
            });
            break;
          case CreditSetupVerifyStatus.COMPLETE_UNCONFIRMED:
            this.analytics.logEvent('credit_otp_completed');
            this.completeRoute().then((resp) => {
              this.handleRouteForward(true, resp);
            });
            break;

          case CreditSetupVerifyStatus.ERROR_NO_RETRY:
            this.handleRouteForward(true, ['credit-setup', 'verify', 'kba', 'intro']);
            break;

          case CreditSetupVerifyStatus.ERROR_FATAL:
            this.handleRoute(true, ['credit-setup', 'creation'], { queryParams: { status: 'VERIFICATION_FAILED' } });
            break;

          default:
            this.processing = false;
            this.codeErrorText = this.translateService.instant('ERROR_OTP_INCORRECT');
            break;
        }
      }
    };

    this.processing = true;

    const otpCode = this.codeForm.get('code')?.value ?? '';

    this.sub.add(this.creditSetupService.VerifyWithOtp(otpCode).subscribe(handleResponse, handleResponse));
  }

  private subscribeAndResendCode() {
    const handleResponse = () => {
      this.processing = false;
      this.codeErrorText = '';
    };

    this.processing = true;
    this.sub.add(this.creditSetupService.startSetup().subscribe(handleResponse, handleResponse));
  }

  handleResendCtaClick() {
    if (this.processing) {
      return;
    }

    this.subscribeAndResendCode();
  }

  handleSecondaryCtaClick() {
    this._bsChatService.showChat();
    this.analytics.logEvent('chat_shown', { source: 'auto' }, true);
  }

  handleCtaClick() {
    if (this.processing) {
      return;
    }

    this.verifyOtpCode();
  }
}
